import { getSupportedLocations } from '../location/helpers';

import {
  routePaths,
  INTERNAL_REFERER,
  TrackingReferers,
  type refererKeys,
  refererMatches,
  ROUTE_PATH_MAPPING,
  ROUTE_PATH_REGEX_MAPPING,
} from './constants';
import { type RouteGroupType } from './types';

type RefererType =
  TrackingReferers | RouteGroupType['route'] | typeof INTERNAL_REFERER;

function referredFromInternal(host: string): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const currentLocation = new URL(window.location.href);
  return host === currentLocation.host;
}

const supportedLocations = getSupportedLocations(
  process.env.NEXT_PUBLIC_SUPPORTED_LOCATIONS || ''
);

const localisedPathRegex = new RegExp(
  `^\\/(${supportedLocations.join('|')})(?=\\/)`
);

function getPath(urlPath: string) {
  const [path, queryParams = ''] = urlPath
    .replace(localisedPathRegex, '')
    .split('?');

  const fullPath = path[path.length - 1] === '/' ? path : `${path}/`;

  const routeGroup = ROUTE_PATH_MAPPING[fullPath] ?? '';

  if (routeGroup) {
    return { routePath: routeGroup, isFallback: false };
  }

  const combinedPath = fullPath + queryParams;

  for (const { regex, routePath, fallback } of ROUTE_PATH_REGEX_MAPPING) {
    if (combinedPath.match(regex)) {
      return { routePath, isFallback: Boolean(fallback) };
    }
  }

  return { routePath: routePaths.NO_MATCH, isFallback: true };
}

export function hasRouteMapping(path: string): boolean {
  return !getPath(path).isFallback;
}

export function getRouteView(path: string): RouteGroupType['route'] {
  return getPath(path).routePath.route;
}

export function getRouteName(path: string): RouteGroupType['groupName'] {
  return getPath(path).routePath.groupName;
}

export function getReferer(refererUrl?: string | null): RefererType {
  if (!refererUrl) {
    return INTERNAL_REFERER;
  }
  const { pathname: path, host, search } = new URL(refererUrl);

  const pathname = `${path}${search}`;

  if (host === null || pathname === null) {
    return INTERNAL_REFERER;
  }

  if (referredFromInternal(host)) {
    return getPath(pathname).routePath.route;
  }

  for (const refererMatch in refererMatches) {
    const { hostMatch, referer } = refererMatches[refererMatch as refererKeys];

    if (host.includes(hostMatch)) {
      if (refererMatch === 'instagram') {
        if (pathname.includes('stories')) {
          return TrackingReferers.INSTAGRAM_STORIES;
        }
      }

      return referer;
    }
  }

  return TrackingReferers.OTHER;
}

export function isExternalReferer(referer?: string): boolean {
  return referer ? !referer.includes('depop.com') : false;
}

export function isInternalReferer(refererUrl?: string | null): boolean {
  if (!refererUrl) {
    return false;
  }
  try {
    const { host } = new URL(refererUrl);
    return referredFromInternal(host) || /(^|\.)depop\.com$/.test(host);
  } catch {
    return false;
  }
}
